import { fetchSearchFamilies } from '@gn-audio/core/api/product-catalogue/fetch-search-families';
import { fetchSearchGroups } from '@gn-audio/core/api/product-catalogue/fetch-search-groups';
import { ISearchItem, ISearchParameters, ISearchResults, ISFCCSearchItem } from '@gn-audio/core/api/product-catalogue/models';
import { IncludeType, ProductType } from '@gn-audio/core/common/enums';
import { smoothScroll } from '@gn-audio/ui/tools/smooth-srcoll';
import { isTablet } from 'src/Foundation/Design/platform/app/utils/is-tablet';

import { IProductFilter, IProductFilterGroup, IProductOrder, PfmMode } from '../models';
import { IProductFilterModuleConfiguration } from '../models/product-filter-module-configuration';
import { IPfmLoadQueueService } from '../services/pfm-load-queue.service';
import { IPfmUrlHelperService } from '../services/pfm-url-helper.service';
import { ProductFilterModuleApiService } from '../services/product-filter-module-api.service';
import { buildFilterQuery } from '../utils/build-filter-query';
import { IProductFilterModuleSpotScope } from './models/product-filter-module-spot.scope';

// [TechDebt] That controller became to be spaghetti. Must be re-written to use components!

/**
 * @deprecated Should be converted into component!
 */
export class PfmController implements ng.IController {
  public static $inject = ['$scope', '$q', 'pfmUrlHelper', 'pfmLoadQueue', 'productFilterModuleApiService'];

  constructor(
    private $scope: IProductFilterModuleSpotScope,
    private $q: ng.IQService,
    private urlHelper: IPfmUrlHelperService,
    private loadQueue: IPfmLoadQueueService,
    private productFilterModuleApiService: ProductFilterModuleApiService,
  ) {
    this.initialize();
    const $configurationIdWatcher = this.$scope.$watch('configurationId', () => {
      $configurationIdWatcher();

      this.loadQueue.add('pfmcontroller#load-configuration');
      this.productFilterModuleApiService.getConfiguration(this.$scope.configurationId)
        .then((configuration: IProductFilterModuleConfiguration) => {
          this.$scope.configuration = configuration;
          this.handleOrderOptions();
          this.handleFilterGroups();
          this.$scope.load();
        })
        .finally(() => {
          this.loadQueue.remove('pfmcontroller#load-configuration');
        });
    });

    const infiniteScrollWatcher = this.$scope.$watch('infiniteScroll', () => {
      this.$scope.infiniteScroll = (this.$scope.infiniteScroll !== 'false');
      infiniteScrollWatcher();
    });
  }

  private initialize() {
    this.$scope.searchOptions = { filter: '', search: this.urlHelper.searchQuery };
    this.$scope.orderOptions = { options: [] };
    this.$scope.pagingOptions = { changed: false, count: 0, currentPage: this.urlHelper.pageNumber, pageSize: 12 };

    this.$scope.loadedProducts = [];
    this.$scope.selectedFilters = [];

    this.$scope.hasLoadedProducts = (): boolean => {
      return this.$scope.loadedProducts.length > 0;
    };

    this.$scope.isGroupType = (): boolean => {
      return this.$scope.configuration.entityType === ProductType.Group;
    };

    this.$scope.areFiltersOrSearchPhraseApplied = (): boolean => {
      const { selectedFilters, searchOptions } = this.$scope;
      return selectedFilters.length > 0 || !!searchOptions.search;
    };

    this.$scope.loading = (): boolean => {
      return !this.loadQueue.isEmpty();
    };

    this.$scope.hasCampaignId = (): boolean => {
      return !!this.$scope.configuration?.campaignId;
    };

    this.$scope.applyFilters = () => {
      smoothScroll(document.querySelector('.product-finder'), {
        callbackAfter: () => {
          this.$scope.pagingOptions.currentPage = 1;
          this.$scope.load(true);
          this.$scope.showFilters = false;
        }
      });
    };

    this.$scope.loadMore = () => {
      if (this.$scope.loading()) {
        return;
      }

      if (this.$scope.pagingOptions.changed && this.$scope.loadedProducts.length === this.$scope.pagingOptions.count) {
        return;
      }

      this.$scope.pagingOptions.currentPage += 1;
      this.$scope.load();
    };

    this.$scope.load = (resetLoadedProducts = false) => {
      const { infiniteScroll, mode, selectedFilters } = this.$scope;
      const { pageSize, currentPage } = this.$scope.pagingOptions;
      if (!infiniteScroll) {
        this.urlHelper.pageNumber = currentPage;
      }

      this.loadQueue.add('pfmcontroller#loadMore');

      const { filterGroups, preFilterQuery, campaignId } = this.$scope.configuration;
      this.$scope.searchOptions.filter = buildFilterQuery(filterGroups, selectedFilters, preFilterQuery);
      const { filter, search } = this.$scope.searchOptions;
      const parameters: ISearchParameters = {
        campaignId,
        filter,
        search,
        skip: pageSize * (currentPage - 1),
        top: pageSize,
        includePrices: mode === PfmMode.Normal || mode === PfmMode.Compare,
      };

      const { current } = this.$scope.orderOptions;
      if (current !== undefined) {
        parameters.orderBy = `${current.property} ${current.direction}`;
      }

      let includeType: IncludeType = IncludeType.Available;
      if (mode !== PfmMode.Normal) {
        includeType = IncludeType.Supported;
      }

      let promise: Promise<ISearchResults<ISearchItem | ISFCCSearchItem> | null>;
      switch (this.$scope.configuration.entityType) {
        case ProductType.Family:
          promise = fetchSearchFamilies(parameters, includeType);
          break;
        case ProductType.Group:
          promise = fetchSearchGroups(parameters, includeType);
        default:
          break;
      }

      if (promise) {
        this.$q
          .when(promise)
          .then(
            (results: ISearchResults<ISearchItem | ISFCCSearchItem> | null) => {
              const searchItems: (ISearchItem | ISFCCSearchItem)[] = results?.items || [];

              if (infiniteScroll && !resetLoadedProducts) {
                searchItems.forEach((resultItem: ISearchItem | ISFCCSearchItem) => {
                  this.$scope.loadedProducts.push(resultItem);
                });
              } else {
                this.$scope.loadedProducts = searchItems;
              }

              this.$scope.loadedProducts.forEach((g: ISearchItem | ISFCCSearchItem) => {
                g.images = (g.images || [])
                  .filter((image: string) => image.match(/.(jpg|jpeg|png|tiff|tif)/gi) !== null);
              });

              this.$scope.pagingOptions.count = results?.count || 0;
              this.$scope.pagingOptions.changed = true;
            },
            () => {
              this.$scope.loadedProducts.length = 0;
            }
          )
          .finally(() => { this.loadQueue.remove('pfmcontroller#loadMore'); });
      }
    };

    this.$scope.searchOptionsChanged = (orderChanged = false) => {
      this.urlHelper.searchQuery = this.$scope.searchOptions.search;
      if (isTablet() && !orderChanged) {
        return;
      }

      smoothScroll(document.querySelector('.product-finder'), {
        callbackAfter: () => {
          this.$scope.pagingOptions.changed = false;
          this.$scope.pagingOptions.count = 0;
          this.$scope.pagingOptions.currentPage = 1;
          this.$scope.load(true);
        }
      });
    };

    this.$scope.toggleFilter = (filterId: string, filterGroup: IProductFilterGroup) => {
      const filterIndex: number = this.$scope.selectedFilters.indexOf(filterId);
      const filter: IProductFilter = filterGroup.filters.filter((f: IProductFilter): boolean => f.id === filterId)[0];

      if (filterIndex === -1) {
        this.$scope.selectedFilters.push(filterId);
        filterGroup.selected++;
        filter.selected = true;
      } else {
        this.$scope.selectedFilters.splice(filterIndex, 1);
        filterGroup.selected--;
        filter.selected = false;
      }

      this.urlHelper.filters = this.$scope.selectedFilters.map((f: string): string => f.substring(f.length - 4));
      this.$scope.searchOptionsChanged();
    };

    this.$scope.orderChanged = (order: IProductOrder) => {
      this.$scope.orderOptions.current = order;
      this.urlHelper.sorter = `${order.property.toLowerCase()}-${order.direction.toLowerCase()}`;
      this.$scope.searchOptionsChanged(true);
    };
  }

  private handleOrderOptions() {
    this.$scope.orderOptions.options = this.$scope.configuration.orders;
    const sorterParts: string[] = this.urlHelper.sorter.split('-');
    if (sorterParts.length > 0) {
      this.$scope.orderOptions.current = this.$scope.orderOptions.options.filter((o: IProductOrder): boolean => {
        return (
          o.property.toLowerCase() === (sorterParts[0] || '').toLowerCase() &&
          o.direction.toLowerCase() === (sorterParts[1] || '').toLowerCase()
        );
      })[0];
      if (this.$scope.orderOptions.current === undefined) {
        this.$scope.orderOptions.current = this.$scope.orderOptions.options[0];
      }
    } else {
      this.$scope.orderOptions.current = this.$scope.orderOptions.options[0];
    }

    if (this.$scope.orderOptions.current !== undefined) {
      this.urlHelper.sorter = `${this.$scope.orderOptions.current.property.toLowerCase()}-${this.$scope.orderOptions.current.direction.toLowerCase()}`;
    }
  }

  private handleFilterGroups() {
    this.$scope.configuration.filterGroups.forEach((fg: IProductFilterGroup) => { fg.selected = 0; });
    for (const filterGroup of this.$scope.configuration.filterGroups) {
      for (const filter of filterGroup.filters) {
        filter.selected = this.urlHelper.filters.some((f: string): boolean => filter.id.indexOf(f) > -1);
        if (filter.selected) {
          this.$scope.selectedFilters.push(filter.id);
          filterGroup.selected++;
        }
      }
    }
  }
}
